"use client";
import React from "react";
import { ThemeUICSSObject } from "theme-ui";

import { Box, BoxSx } from "..";
import { useThemeWithoutTypes } from "../Theme";
import { GridVariant } from "./GridTypes";

type GridElementProps = {
  fillGapEnd: boolean;
  fillGapStart: boolean;
  initial: boolean;
  variant: GridVariant;
  wrap: boolean;
} & React.ComponentProps<typeof Box>;

const getMarginInlineStyle = (gutter: number) => {
  return `-${gutter / 2}px`;
};

const getInitialMarginInlineEndStyle = (margin: number, gutter: number) => {
  return `${margin - gutter / 2}px`;
};

const getInitialMarginInlineStartStyle = (
  maxWidth: number,
  margin: number,
  gutter: number
) => {
  const gutterPadding = gutter / 2;
  return `max(50vw - (${gutterPadding + maxWidth / 2}px), ${
    margin - gutterPadding
  }px)`;
};

export const GridElement: React.FC<GridElementProps> = ({
  fillGapEnd,
  fillGapStart,
  initial,
  sx,
  variant,
  wrap,
  ...props
}) => {
  const theme = useThemeWithoutTypes();
  const styles = React.useMemo<ThemeUICSSObject>(() => {
    const {
      sizes: {
        containerDesktopMargin,
        containerDesktopMaxWidth,
        containerMobileMargin,
        containerMobileMaxWidth,
        containerTabletMargin,
        containerTabletMaxWidth,
        gridDesktopColumnGutter,
        gridMobileColumnGutter,
        gridTabletColumnGutter
      }
    } = theme;

    return {
      boxSizing: "border-box",
      display: "flex",
      flexGrow: 1,
      flexWrap: wrap ? "wrap" : "nowrap",
      justifyContent: "flex-start",
      marginInlineEnd: initial
        ? [
            getInitialMarginInlineEndStyle(
              containerMobileMargin,
              gridMobileColumnGutter
            ),
            getInitialMarginInlineEndStyle(
              containerTabletMargin,
              gridTabletColumnGutter
            ),
            getInitialMarginInlineEndStyle(
              containerDesktopMargin,
              gridDesktopColumnGutter
            )
          ]
        : [
            getMarginInlineStyle(gridMobileColumnGutter),
            getMarginInlineStyle(gridTabletColumnGutter),
            getMarginInlineStyle(gridDesktopColumnGutter)
          ],
      marginInlineStart: initial
        ? [
            getInitialMarginInlineStartStyle(
              containerMobileMaxWidth,
              containerMobileMargin,
              gridMobileColumnGutter
            ),
            getInitialMarginInlineStartStyle(
              containerTabletMaxWidth,
              containerTabletMargin,
              gridTabletColumnGutter
            ),
            getInitialMarginInlineStartStyle(
              containerDesktopMaxWidth,
              containerDesktopMargin,
              gridDesktopColumnGutter
            )
          ]
        : [
            getMarginInlineStyle(gridMobileColumnGutter),
            getMarginInlineStyle(gridTabletColumnGutter),
            getMarginInlineStyle(gridDesktopColumnGutter)
          ],
      maxWidth:
        (variant === "primary" || variant === "uniform-12") && initial
          ? [
              `${containerMobileMaxWidth + gridMobileColumnGutter}px`,
              `${containerTabletMaxWidth + gridTabletColumnGutter}px`,
              `${containerDesktopMaxWidth + gridDesktopColumnGutter}px`
            ]
          : "none",
      paddingInlineEnd: [
        fillGapEnd ? `${theme.sizes.gridMobileColumnGutter}px` : 0,
        fillGapEnd ? `${theme.sizes.gridTabletColumnGutter}px` : 0,
        fillGapEnd ? `${theme.sizes.gridDesktopColumnGutter}px` : 0
      ],
      paddingInlineStart: [
        fillGapStart ? `${theme.sizes.gridMobileColumnGutter}px` : 0,
        fillGapStart ? `${theme.sizes.gridTabletColumnGutter}px` : 0,
        fillGapStart ? `${theme.sizes.gridDesktopColumnGutter}px` : 0
      ],
      // Hack so the grid renders well in the Storybook docs
      ".docs-story &": initial
        ? {
            marginInlineEnd: "auto",
            marginInlineStart: "auto"
          }
        : null
    };
  }, [fillGapEnd, fillGapStart, initial, theme, variant, wrap]);

  return (
    <Box
      sx={
        {
          ...sx,
          ...styles
          // This element requires calculated sizing values and can't use
          // the Box sx props from the theme.
        } as unknown as BoxSx
      }
      {...props}
    />
  );
};
